import { TableColumnsType } from 'antd';
import moment from 'moment';
import { ManOutlined, WomanOutlined } from '@ant-design/icons';
import { styled } from 'styled-components';
import { flexMixin, textMixin } from '@styles/mixins';
import { StyledTemplate } from './StyledTemplate';
import { ITemplate, Iline } from '@entities/card/types';
import { IClient } from '@types';
import { ITransaction } from '@entities/logs';

export const getTransactionColumns = (
    handlerClickId: (
        id: number,
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => void,
    handleClickCheckId: (
        id: ITransaction,
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => void,
    templates: ITemplate[],
    expanded: boolean,
    setExpanded: (data: boolean) => void
): TableColumnsType<ITransaction> => {
    return [
        {
            title: <div style={{ marginLeft: '5px' }}>Дата операции</div>,
            dataIndex: 'date',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            width: '8%',

            render: record => {
                return (
                    <DateWrapper>
                        <StyledSpan>
                            {record ? moment(record).format('DD.MM.YYYY') : '-'}
                        </StyledSpan>
                        <StyledSpan>
                            {record ? moment(record).format('HH:mm:ss') : '-'}
                        </StyledSpan>
                    </DateWrapper>
                );
            },
        },
        {
            title: '№',
            dataIndex: 'client',
            width: '6%',
            align: 'center',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],

            render: (client: IClient) => (
                <StyledLink
                    onClick={e => handlerClickId(client.id, e)}
                    role="button"
                    tabIndex={0}
                >
                    {client.id}
                </StyledLink>
            ),
        },
        {
            title: 'ФИО',
            dataIndex: 'client',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            width: '20%',
            render: client => {
                if (!client) return '-';
                const fullName = `${client.lastname} ${client.firstname}`;
                return <TextWrapper>{fullName}</TextWrapper>;
            },
        },
        {
            title: 'Пол',
            dataIndex: ['client', 'sex'],
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            width: '6%',
            filters: [
                {
                    text: 'муж',
                    value: 1,
                },
                {
                    text: 'жен',
                    value: 2,
                },
            ],
            onFilter: (value, record) => record?.client.sex === value,

            render: (_, { client }) => {
                return client?.sex === 1 ? (
                    <ManOutlined
                        style={{ color: '#52c41a', fontSize: '14px' }}
                    />
                ) : (
                    <WomanOutlined
                        style={{ color: '#eb2f96', fontSize: '14px' }}
                    />
                );
            },
        },
        {
            title: 'Макет',
            width: '10%',
            dataIndex: 'template',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            filters: templates?.map(temp => ({
                text: temp.name,
                value: temp.id,
            })),
            onFilter: (value, record) => record.template.id === value,

            render: (template: ITemplate) => (
                <StyledTemplate key={template.id} template={template} ind={1} />
            ),
        },

        {
            title: 'Наименование позиций',
            width: '30%',
            dataIndex: 'lines',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            render: (lines: Iline[]) => {
                const visibleItems = expanded ? lines : lines.slice(0, 3);
                const hasMore = lines.length > 3;

                return (
                    <LinesContainer>
                        {visibleItems.map((template, i) => {
                            const data = `${template.name} : ${template.quantity} шт`;
                            return (
                                <LinesWrapper key={template.code + i}>
                                    <span>{data}</span>
                                </LinesWrapper>
                            );
                        })}
                        {hasMore && !expanded && (
                            <ShowMore onClick={() => setExpanded(true)}>
                                ...Еще ({lines.length - 3})
                            </ShowMore>
                        )}
                        {expanded && (
                            <ShowLess onClick={() => setExpanded(false)}>
                                Свернуть
                            </ShowLess>
                        )}
                    </LinesContainer>
                );
            },
        },
        {
            title: 'Номер чека',
            dataIndex: 'checknum',

            width: '7%',
            render: (_, record) => {
                return (
                    <ChequeWrapper onClick={e => handleClickCheckId(record, e)}>
                        {record.checknum}
                    </ChequeWrapper>
                );
            },
        },
        {
            title: 'Сумма чека',
            dataIndex: 'lines',

            width: '7%',
            render: (lines: Iline[], record: ITransaction) => {
                if (!lines || !Array.isArray(lines)) {
                    return <TextWrapper>-</TextWrapper>;
                }

                const total = lines.reduce((sum, line) => {
                    return sum + line.price * line.quantity;
                }, 0);
                const displayTotal = record.type === 2 ? -total : total;

                return <TextWrapper>{displayTotal.toFixed(2)}</TextWrapper>;
            },
        },

        {
            title: 'Оплачено',
            dataIndex: 'payed',

            width: '7%',
            render: (payed: number) => {
                return <TextWrapper>{payed.toFixed(2)}</TextWrapper>;
            },
        },
    ];
};
const StyledSpan = styled.div`
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    display: block;
    width: 100%;
    ${flexMixin('column', 'center', 'flex-start')};
`;

const StyledLink = styled.a`
    cursor: pointer;
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    color: #1677ff;
`;
const TextWrapper = styled.div`
    ${textMixin({
        fontSize: '14px',
        fontWeight: 500,
    })};
`;
const LinesWrapper = styled.div`
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    ${flexMixin('column', 'center', 'flex-start')};
    gap: 5px;
`;
const ChequeWrapper = styled.div`
    ${textMixin({
        fontSize: '14px',
        fontWeight: 500,
    })};
    cursor: pointer;
    color: #1677ff;
`;
const DateWrapper = styled.div`
    ${flexMixin('column', 'center', 'flex-start')};
    flex-wrap: wrap;
    margin-left: 5px;
`;
const LinesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const ShowMore = styled.span`
    color: #1677ff;
    cursor: pointer;
    font-size: 14px;
    margin-top: 5px;
    &:hover {
        text-decoration: underline;
    }
`;

const ShowLess = styled.span`
    color: #1677ff;
    cursor: pointer;
    font-size: 14px;
    margin-top: 5px;
    &:hover {
        text-decoration: underline;
    }
`;
